import { ReleaseType } from '@ibk/shared-types';
import { BeneficiaryStatusInProduct, } from '../types';
import { toCardIssuedFilter } from './cardStatusUtils';
export function isActiveStatus(statusInProduct) {
    return statusInProduct === BeneficiaryStatusInProduct.ACTIVE;
}
const APPROVED_ANALYSIS_STATUS = 'APPROVED';
function hasAnalysisInfo(beneficiary) {
    return beneficiary.kycStatus !== undefined || beneficiary.identityStatus !== undefined;
}
export function getBeneficiaryAnalysisStatus(beneficiary) {
    if (!hasAnalysisInfo(beneficiary)) {
        return null;
    }
    if (beneficiary.kycStatus === 'REJECTED') {
        return 'REJECTED';
    }
    if (beneficiary.kycStatus !== APPROVED_ANALYSIS_STATUS) {
        return 'PENDING';
    }
    return 'APPROVED';
}
export function isBeneficiarySelectable(beneficiary) {
    const analysisStatus = getBeneficiaryAnalysisStatus(beneficiary);
    return analysisStatus === null || analysisStatus === 'APPROVED';
}
export function getUnformatedValue(valueInString) {
    const numericValue = valueInString ? Number(valueInString.replace(/\D/g, '')) : null;
    if (!numericValue) {
        return 0;
    }
    return numericValue / 100;
}
export function validateAndFormatDays(value) {
    const numericValue = value.replace(/\D/g, '');
    if (numericValue === '') {
        return '';
    }
    const days = parseInt(numericValue) || 0;
    const validDays = Math.max(0, Math.min(99, days));
    return validDays.toString();
}
export function hasAnyFilter(filterData, includeName = false) {
    return (filterData.startDate !== undefined ||
        filterData.endDate !== undefined ||
        filterData.contractType.length > 0 ||
        filterData.cardStatus.length > 0 ||
        filterData.jobRole.length > 0 ||
        filterData.costCenter.length > 0 ||
        filterData.department.length > 0 ||
        filterData.category.length > 0 ||
        filterData.employmentBond.length > 0 ||
        filterData.branch.length > 0 ||
        filterData.union.length > 0 ||
        (includeName && filterData.name !== undefined && filterData.name.trim() !== ''));
}
function addArrayFieldIfNotEmpty(request, fieldName, value) {
    if (value.length > 0) {
        request[fieldName] = value;
    }
}
function addStringFieldIfDefined(request, fieldName, value) {
    if (value) {
        request[fieldName] = value;
    }
}
function addCardIssuedIfSingle(request, cardStatus) {
    const cardIssued = toCardIssuedFilter(cardStatus);
    if (cardIssued !== undefined) {
        request.cardIssued = cardIssued;
    }
}
function buildRequestFromFilterData(filterData) {
    const request = {};
    const trimmedName = filterData.name?.trim();
    if (trimmedName) {
        request.name = trimmedName;
    }
    addArrayFieldIfNotEmpty(request, 'contractType', filterData.contractType);
    addArrayFieldIfNotEmpty(request, 'jobRole', filterData.jobRole);
    addArrayFieldIfNotEmpty(request, 'costCenter', filterData.costCenter);
    addArrayFieldIfNotEmpty(request, 'department', filterData.department);
    addArrayFieldIfNotEmpty(request, 'category', filterData.category);
    addArrayFieldIfNotEmpty(request, 'employmentBond', filterData.employmentBond);
    addArrayFieldIfNotEmpty(request, 'branch', filterData.branch);
    addArrayFieldIfNotEmpty(request, 'union', filterData.union);
    addCardIssuedIfSingle(request, filterData.cardStatus);
    addStringFieldIfDefined(request, 'startDate', filterData.startDate);
    addStringFieldIfDefined(request, 'endDate', filterData.endDate);
    return request;
}
export function convertFilterDataToSearchRequest(filterData, selectedBeneficiaries) {
    if (!filterData) {
        return undefined;
    }
    if (!hasAnyFilter(filterData, true) && !selectedBeneficiaries?.length) {
        return undefined;
    }
    const request = buildRequestFromFilterData(filterData);
    if (selectedBeneficiaries?.length) {
        request.selected = selectedBeneficiaries;
    }
    return request;
}
export function updateBeneficiariesDiscounts(beneficiaries, discountValue, isBeneficiaryDiscountEnabled, isIndividualDiscount) {
    if (!isBeneficiaryDiscountEnabled || !isIndividualDiscount || !discountValue) {
        return beneficiaries.map(beneficiary => {
            if (isActiveStatus(beneficiary.statusInProduct)) {
                return {
                    ...beneficiary,
                    productDiscountValue: 0,
                };
            }
            return beneficiary;
        });
    }
    const discountNumericValue = getUnformatedValue(discountValue) || 0;
    return beneficiaries.map(beneficiary => {
        if (isActiveStatus(beneficiary.statusInProduct)) {
            return {
                ...beneficiary,
                productDiscountValue: discountNumericValue,
            };
        }
        return beneficiary;
    });
}
export function updateBeneficiariesValues(beneficiaries, globalNumericValue, releaseType, businessDays) {
    const isDaily = releaseType === ReleaseType.DAILY;
    return beneficiaries.map(beneficiary => {
        if (isActiveStatus(beneficiary.statusInProduct)) {
            if (isDaily) {
                const daysQuantity = businessDays ?? beneficiary.productDaysQuantity ?? 0;
                const totalValue = daysQuantity > 0 ? globalNumericValue * daysQuantity : 0;
                return {
                    ...beneficiary,
                    productValue: totalValue,
                    productDaysQuantity: daysQuantity,
                    importedFields: undefined,
                };
            }
            else {
                return {
                    ...beneficiary,
                    productValue: globalNumericValue,
                    productDaysQuantity: 0,
                    importedFields: undefined,
                };
            }
        }
        return beneficiary;
    });
}
export function shouldUpdateBeneficiaries(releaseType, globalValue, beneficiaries, defaultCurrencyValue) {
    return !!(beneficiaries &&
        beneficiaries.length > 0 &&
        releaseType &&
        globalValue &&
        globalValue !== defaultCurrencyValue);
}
export function mergeSelectedIntoBeneficiaryRows(apiRows, selected) {
    const byId = new Map(apiRows.map(b => [b.id, b]));
    return selected
        .map(sel => {
        const row = byId.get(sel.beneficiaryId);
        if (!row) {
            return null;
        }
        return {
            ...row,
            productValue: sel.value,
            productDaysQuantity: sel.daysQuantity ?? row.productDaysQuantity ?? 0,
            productDiscountValue: sel.discountValue ?? row.productDiscountValue ?? 0,
            statusInProduct: BeneficiaryStatusInProduct.ACTIVE,
        };
    })
        .filter((b) => b != null);
}
